import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { calculateCvCompletionScore, mergeImportedDraftPreservingUserEdits, normalizeCvDraft, createDefaultCvDraft, sanitizeCvDraft } from "@/lib/cvDraftContract";
import { logAppError, normalizeApiError } from "@/lib/api";
import { normalizeProviderName } from "@/lib/cvImportProvider";
import {
    createParseJob,
    getParseJobStatus,
} from "@/lib/recruitmentApi";
import {
    createCandidateActions,
    createSkillsActions,
    createExperienceActions,
    createProfileActions,
} from "@/stores/cvBuilder/domainActions";
import { fetchDraftById, persistDraft, requestGenerateDocx, requestGeneratePdf, requestPreviewPdf, requestPreviewPdfPayload, requestUserValidation } from "@/stores/cvBuilder/apiLayer";
import { createCVDraft } from "@/lib/recruitmentApi";
import { applyLoadedDraftMeta } from "@/stores/cvBuilder/editorState";
import { clearLocalDraft, readLocalDraft, shouldPersistLocalDraft, writeLocalDraft } from "@/stores/cvBuilder/autosave";
import { getAiErrorUserMessage } from "@/lib/cvWarningMessages";
import { toast } from "react-hot-toast";
import { saveFormBackup } from "@/lib/authSession";

const CvBuilderContext = createContext(null);

export const buildPdfFileName = (draft) => {
    const trigram = String(draft?.trigram || "CAN").trim().toUpperCase() || "CAN";
    const title = String(draft?.candidate?.title || "Consultant Tech")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^A-Za-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "") || "CV";
    return `DC_${trigram}_${title.toUpperCase()}_LENVIA.pdf`;
};

export const buildDocxFileName = (draft) => buildPdfFileName(draft).replace(/\.pdf$/i, ".docx");

export const ACTIVE_PARSE_JOB_STORAGE_KEY = "lenvia_active_cv_parse_job";
export const CV_PARSE_POLLING_INTERVAL_MS = 1200;
export const MAX_CONSECUTIVE_POLL_ERRORS = 5;

const clampScore = (value) => {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) {
        return 0;
    }
    return Math.max(0, Math.min(100, parsed));
};

const resolveQualityStatus = (qualityStatus, manualReviewRequired) => {
    const normalized = String(qualityStatus || "").toLowerCase();
    if (["ok", "warning", "rejected"].includes(normalized)) {
        return normalized;
    }
    return manualReviewRequired ? "warning" : "ok";
};

const pickImportPayload = (rawPayload) => {
    const payload = rawPayload && typeof rawPayload === "object" ? rawPayload : {};
    if (payload.result && typeof payload.result === "object") {
        return payload.result;
    }
    if (
        payload.data
        && typeof payload.data === "object"
        && ("success" in payload.data || "draft" in payload.data || "parsed_cv" in payload.data)
    ) {
        return payload.data;
    }
    return payload;
};

const shouldEmitCvImportTelemetry = process.env.NODE_ENV !== "test";

const getFileExtension = (fileName = "") => {
    const normalized = String(fileName || "").toLowerCase();
    const lastDot = normalized.lastIndexOf(".");
    if (lastDot === -1) {
        return "unknown";
    }
    return normalized.slice(lastDot + 1) || "unknown";
};

export const STATUS_OPEN = "OPEN";
export const STATUS_CONFIRMED = "CONFIRMED";
export const STATUS_CORRECTED = "CORRECTED";
export const STATUS_REASSIGNED = "REASSIGNED";
export const STATUS_DISMISSED = "DISMISSED";
export const STATUS_REOPENED = "REOPENED";

export const isAnomalyActive = (status) => {
    const s = String(status || "").toUpperCase();
    return ["OPEN", "REOPENED", "PENDING_REVIEW", "ACTIVE", ""].includes(s);
};

export const isAnomalyTreated = (status) => {
    const s = String(status || "").toUpperCase();
    return ["CONFIRMED", "CORRECTED", "REASSIGNED", "DISMISSED", "VERIFIED", "RESOLVED", "ALREADY_INTEGRATED"].includes(s);
};

export const computeVerificationProgress = (anomalies = []) => {
    const list = Array.isArray(anomalies) ? anomalies : [];
    const total = list.length;
    const treated = list.filter((a) => isAnomalyTreated(a?.status)).length;
    const remaining = Math.max(0, total - treated);
    const percentage = total > 0 ? Math.round((treated / total) * 100) : 100;
    return {
        total,
        treated,
        remaining,
        percentage,
        is_complete: remaining === 0,
    };
};

export const computeDocumentQualityProgress = (documentQuality, fallbackProgress = null, anomalies = []) => {
    if (documentQuality && typeof documentQuality === "object") {
        const total = Number(documentQuality.total_facts) || 0;
        const treated = Number(documentQuality.automatically_decided ?? documentQuality.automatically_verified) || 0;
        const remaining = Number(documentQuality.human_review_required) || 0;
        return { total, treated, remaining, percentage: total > 0 ? Math.round((treated / total) * 100) : 100, is_complete: remaining === 0 };
    }
    if (fallbackProgress && typeof fallbackProgress === "object") {
        const total = fallbackProgress.total_count ?? fallbackProgress.total ?? anomalies.length;
        const treated = fallbackProgress.treated_count ?? fallbackProgress.treated ?? 0;
        const remaining = fallbackProgress.remaining_count ?? fallbackProgress.remaining ?? Math.max(0, total - treated);
        return { total, treated, remaining, percentage: fallbackProgress.percentage ?? (total > 0 ? Math.round((treated / total) * 100) : 100), is_complete: remaining === 0 };
    }
    return computeVerificationProgress(anomalies);
};

const logCvImportTelemetry = (event, payload = {}, level = "info") => {
    if (!shouldEmitCvImportTelemetry) {
        return;
    }

    const safePayload = {
        event,
        ...payload,
    };

    if (level === "error") {
        console.error("[cv-builder-import]", safePayload);
        return;
    }
    if (level === "warn") {
        console.warn("[cv-builder-import]", safePayload);
        return;
    }
    console.info("[cv-builder-import]", safePayload);
};

/**
 * Recomputes scoring dimensions without artificial inflation from human confirmations.
 * Technical extraction fidelity is grounded in documentary evidence, not recruiter clicks.
 */
const recomputeScoringFromAnomalies = (currentDimensions, _anomalies) => {
    if (!currentDimensions || typeof currentDimensions !== "object") return null;

    // P2: Extraction fidelity scores are documentary and immutable.
    // Human anomaly resolution (CONFIRMED/DISMISSED) does NOT change
    // the extraction quality score — only CORRECTED does, and that
    // requires backend revalidation.
    // We preserve the backend-computed dimensions as-is.
    const dimScore = (dim) => {
        const s = dim?.score;
        return typeof s === "number" && s >= 0 ? s : null;
    };

    const coverageScore = dimScore(currentDimensions.content_coverage) ?? 100;
    const factualScore = dimScore(currentDimensions.factual_fidelity) ?? 100;
    const structureScore = dimScore(currentDimensions.structural_assignment) ?? 100;
    const chronoScore = dimScore(currentDimensions.chronological_coherence) ?? 100;
    const integrityScore = dimScore(currentDimensions.text_integrity) ?? 100;
    const traceabilityScore = dimScore(currentDimensions.traceability) ?? 100;

    const nextDimensions = { ...currentDimensions };

    // P2 weights: 25/25/15/15/10/10
    const weights = {
        content_coverage: 0.25,
        factual_fidelity: 0.25,
        structural_assignment: 0.15,
        chronological_coherence: 0.15,
        text_integrity: 0.10,
        traceability: 0.10,
    };
    const scores = {
        content_coverage: coverageScore,
        factual_fidelity: factualScore,
        structural_assignment: structureScore,
        chronological_coherence: chronoScore,
        text_integrity: integrityScore,
        traceability: traceabilityScore,
    };

    // Handle N/A dimensions: redistribute weight
    let applicableWeightSum = 0;
    for (const [dim, w] of Object.entries(weights)) {
        if (dimScore(currentDimensions[dim]) !== null) {
            applicableWeightSum += w;
        }
    }
    if (applicableWeightSum <= 0) applicableWeightSum = 1.0;

    let nextTotalScore = 0;
    for (const [dim, w] of Object.entries(weights)) {
        if (dimScore(currentDimensions[dim]) !== null) {
            nextTotalScore += (w / applicableWeightSum) * scores[dim];
        }
    }
    nextTotalScore = Math.max(0, Math.min(100, Math.round(nextTotalScore)));

    return { nextDimensions, nextTotalScore };
};

const normalizeImportResponse = (rawPayload) => {
    const payload = pickImportPayload(rawPayload);
    const draft = payload.draft ?? payload.parsed_cv ?? null;
    const warnings = Array.isArray(payload.warnings)
        ? payload.warnings.map((item) => String(item || "").trim()).filter(Boolean)
        : [];

    const hasExplicitSuccess = typeof payload.success === "boolean";
    const hasExplicitManualReview = typeof payload.manual_review_required === "boolean"
        || typeof payload.manualReviewRequired === "boolean";
    const rawManualReview = payload.manual_review_required === true || payload.manualReviewRequired === true;
    const success = hasExplicitSuccess ? payload.success : Boolean(draft);
    const safeSuccess = success && Boolean(draft);
    const rawQualityStatus = payload.quality_status ?? payload.qualityStatus ?? payload?.quality?.status;
    let qualityStatus = resolveQualityStatus(rawQualityStatus, rawManualReview);
    if (!safeSuccess && !rawQualityStatus) {
        qualityStatus = "rejected";
    }
    const errorCode = typeof payload.error_code === "string" && payload.error_code.trim() !== ""
        ? payload.error_code.trim()
        : (typeof payload.code === "string" && payload.code.trim() !== "" ? payload.code.trim() : null);
    const errorMessage = typeof payload.error_message === "string" && payload.error_message.trim() !== ""
        ? payload.error_message.trim()
        : (typeof payload.message === "string" && payload.message.trim() !== "" ? payload.message.trim() : null);
    const providerPrimary = normalizeProviderName(payload.provider_primary, "unknown");
    const providerUsed = normalizeProviderName(payload.provider_used, providerPrimary);
    const providerFallbackUsed = payload.provider_fallback_used === true;
    const providerFallbackReason = typeof payload.provider_fallback_reason === "string"
        ? payload.provider_fallback_reason.trim()
        : "";
    const modelUsed = String(payload.model_used || "").trim();
    const anomalies = Array.isArray(payload.anomalies) ? payload.anomalies : [];
    const scoringDimensions = payload.scoring_dimensions || null;
    const structuredWarnings = Array.isArray(payload.structured_warnings)
        ? payload.structured_warnings
        : (Array.isArray(payload.warning_details) ? payload.warning_details : []);
    const rawValidationStatus = String(payload.validation_status || "").toUpperCase();
    const validationStatus = ["READY", "READY_WITH_WARNINGS", "REVIEW_RECOMMENDED", "CORRECTION_REQUIRED", "BLOCKED"].includes(rawValidationStatus)
        ? rawValidationStatus
        : (rawManualReview ? "CORRECTION_REQUIRED" : (qualityStatus === "warning" ? "REVIEW_RECOMMENDED" : "READY"));

    const extractionFidelity = clampScore(
        payload.extraction_fidelity ?? payload.fidelity_score ?? payload.confidence_score
    );
    const documentQuality = draft?.source_metadata?.document_quality || payload.document_quality || null;
    const verificationProgress = computeDocumentQualityProgress(documentQuality, payload.verification_progress, anomalies);

    return {
        success: safeSuccess,
        draft: safeSuccess ? draft : null,
        error_code: errorCode,
        error_message: errorMessage,
        warnings,
        structured_warnings: structuredWarnings,
        anomalies,
        scoring_dimensions: scoringDimensions,
        active_anomalies_count: typeof payload.active_anomalies_count === "number"
            ? payload.active_anomalies_count
            : verificationProgress.remaining,
        fidelity_score: extractionFidelity,
        extraction_fidelity: extractionFidelity,
        verification_progress: verificationProgress,
        source_section_coverage: payload.source_section_coverage || null,
        experience_diagnostics: payload.experience_diagnostics || null,
        manual_review_required: hasExplicitManualReview
            ? rawManualReview
            : (safeSuccess && qualityStatus !== "ok"),
        quality_status: qualityStatus,
        validation_status: validationStatus,
        confidence_score: extractionFidelity,
        provider_primary: providerPrimary,
        provider_used: providerUsed,
        provider_fallback_used: providerFallbackUsed,
        provider_fallback_reason: providerFallbackReason,
        model_used: modelUsed,
        document_quality: documentQuality,
        draft_id: payload.draft_id || null,
        content_revision: Number(payload.content_revision || payload.contentRevision) || 1,
        owner_id: payload.owner_id || null,
        engine_version: payload.engine_version || null,
    };
};

const normalizeImportError = (error) => {
    const apiError = normalizeApiError(error, "Erreur lors de l'analyse du CV");
    const backendPayload = error?.response?.data && typeof error.response.data === "object"
        ? normalizeImportResponse(error.response.data)
        : null;
    const isTimeout = apiError.code === "ECONNABORTED" || String(error?.message || "").toLowerCase().includes("timeout");
    const timeoutMessage = "L'analyse du CV prend plus de temps que prévu. Veuillez réessayer avec un fichier plus léger ou vérifier votre connexion.";

    const rawErrorCode = backendPayload?.error_code || (isTimeout ? "AI_TIMEOUT" : "cv_import_failed");
    const aiMappedMessage = getAiErrorUserMessage(rawErrorCode);
    const resolvedErrorMessage = backendPayload?.error_message || aiMappedMessage || (isTimeout ? timeoutMessage : apiError.message);

    return {
        success: false,
        draft: null,
        error_code: rawErrorCode,
        error_message: resolvedErrorMessage,
        warnings: backendPayload?.warnings || [],
        manual_review_required: backendPayload?.manual_review_required || false,
        quality_status: backendPayload?.quality_status || "rejected",
        confidence_score: backendPayload?.confidence_score || 0,
        provider_primary: normalizeProviderName(backendPayload?.provider_primary, "unknown"),
        provider_used: normalizeProviderName(backendPayload?.provider_used, backendPayload?.provider_primary || "unknown"),
        provider_fallback_used: backendPayload?.provider_fallback_used === true,
        provider_fallback_reason: backendPayload?.provider_fallback_reason || "",
        model_used: String(backendPayload?.model_used || "").trim(),
    };
};

export const CvBuilderProvider = ({ children, persistLocalDraft = true, resumeImport = true, jobStorageKey = ACTIVE_PARSE_JOB_STORAGE_KEY }) => {
    const [draft, setDraft] = useState(() => createDefaultCvDraft());
    const [draftId, setDraftId] = useState(null);
    const [contentRevision, setContentRevision] = useState(null);
    const [status, setStatus] = useState("new");
    const [completionScore, setCompletionScore] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Import Flow States
    const [importMode, setImportMode] = useState("none"); // "none", "selection", "uploading", "scoring", "review"
    const [parseScore, setParseScore] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [importProgress, setImportProgress] = useState(null);
    const applyImportProgress = useCallback((nextProgress) => {
        if (!nextProgress || typeof nextProgress !== "object") return;
        setImportProgress((current) => {
            const currentSequence = Number(current?.sequence ?? -1);
            const nextSequence = Number(nextProgress?.sequence ?? 0);
            return nextSequence >= currentSequence ? nextProgress : current;
        });
    }, []);
    const [importError, setImportError] = useState(null);
    const [importWarnings, setImportWarnings] = useState([]);
    const [manualReviewRequired, setManualReviewRequired] = useState(false);
    const [validationStatus, setValidationStatus] = useState("READY");
    const [lastImportResult, setLastImportResult] = useState(null);
    const [reviewConfirmed, setReviewConfirmed] = useState(false);
    const [userValidated, setUserValidated] = useState(false);
    const [forceRefresh, setForceRefresh] = useState(false);
    const [cacheMetadata, setCacheMetadata] = useState(null);
    const [duplicatePrompt, setDuplicatePrompt] = useState({ show: false, existingDrafts: [], payload: null });
    const [previewPageCount, setPreviewPageCount] = useState(1);
    const [anomalies, setAnomalies] = useState([]);
    const [scoringDimensions, setScoringDimensions] = useState(null);
    const [verificationProgress, setVerificationProgress] = useState({ total: 0, treated: 0, remaining: 0, percentage: 100, is_complete: true });
    const latestSavedDraftRef = useRef(null);

    const loadLocalDraft = useCallback(() => {
        try {
            const saved = readLocalDraft();
            if (!saved) {
                return false;
            }
            setDraft(normalizeCvDraft(saved));
            toast.success("Brouillon local chargé");
            return true;
        } catch (error) {
            console.error("Failed to parse local draft", error);
            return false;
        }
    }, [setDraft]);

    useEffect(() => {
        if (persistLocalDraft && !draftId && shouldPersistLocalDraft(draft)) {
            writeLocalDraft(draft);
        }
    }, [draft, draftId, persistLocalDraft]);

    useEffect(() => {
        setCompletionScore(calculateCvCompletionScore(draft));
    }, [draft]);

    const loadDraft = useCallback(
        async (id) => {
            setIsLoading(true);
            try {
                const data = await fetchDraftById(id);
                const nextMeta = applyLoadedDraftMeta(data, id);

                setDraft(normalizeCvDraft(data.draft));
                setDraftId(nextMeta.draftId);
                setContentRevision(Number(data?.content_revision) || null);
                setStatus(nextMeta.status);
                setUserValidated(data?.user_validation?.status === "VALIDATED");
                setCompletionScore(nextMeta.completionScore);
                const analysis = data?.draft?.source_metadata?.analysis;
                if (analysis && typeof analysis === "object") {
                    const loadedAnomalies = Array.isArray(analysis.anomalies) ? analysis.anomalies : [];
                    const restoredStatus = ["READY", "READY_WITH_WARNINGS", "REVIEW_RECOMMENDED", "CORRECTION_REQUIRED", "BLOCKED"].includes(String(analysis.validation_status || "").toUpperCase())
                        ? String(analysis.validation_status).toUpperCase()
                        : "READY";
                    const restoredProgress = analysis.verification_progress && typeof analysis.verification_progress === "object"
                        ? {
                            total: analysis.verification_progress.total_count ?? analysis.verification_progress.total ?? loadedAnomalies.length,
                            treated: analysis.verification_progress.treated_count ?? analysis.verification_progress.treated ?? 0,
                            remaining: analysis.verification_progress.remaining_count ?? analysis.verification_progress.remaining ?? loadedAnomalies.length,
                            percentage: analysis.verification_progress.percentage ?? 0,
                            is_complete: (analysis.verification_progress.remaining_count ?? analysis.verification_progress.remaining ?? loadedAnomalies.length) === 0,
                        }
                        : computeVerificationProgress(loadedAnomalies);

                    const restoredFidelity = clampScore(analysis.extraction_fidelity ?? analysis.confidence_score);
                    setValidationStatus(restoredStatus);
                    setParseScore(restoredFidelity);
                    setVerificationProgress(restoredProgress);
                    setImportWarnings(Array.isArray(analysis.warnings) ? analysis.warnings : []);
                    setAnomalies(loadedAnomalies);
                    setScoringDimensions(analysis.scoring_dimensions || null);
                    setManualReviewRequired(restoredStatus === "CORRECTION_REQUIRED");
                    setLastImportResult({
                        ...analysis,
                        extraction_fidelity: restoredFidelity,
                        verification_progress: restoredProgress,
                        source_section_coverage: analysis.source_section_coverage || null,
                        document_quality: data?.draft?.source_metadata?.document_quality || null,
                    });
                } else {
                    const legacyStatus = String(data?.validation_status || "").toUpperCase();
                    const restoredStatus = ["READY", "REVIEW_RECOMMENDED", "CORRECTION_REQUIRED"].includes(legacyStatus)
                        ? legacyStatus
                        : (data?.manual_review_required ? "CORRECTION_REQUIRED" : "READY");
                    setValidationStatus(restoredStatus);
                    setManualReviewRequired(restoredStatus === "CORRECTION_REQUIRED");
                    const restoredScore = clampScore(data?.extraction_fidelity ?? data?.confidence_score);
                    setParseScore(restoredScore);
                    setVerificationProgress({ total: 0, treated: 0, remaining: 0, percentage: 100, is_complete: true });
                }
                toast.success("Brouillon chargé");
            } catch (error) {
                console.error("Error loading draft", error);
                toast.error("Erreur lors du chargement du brouillon");
            } finally {
                setIsLoading(false);
            }
        },
        [setDraft, setDraftId, setStatus, setCompletionScore],
    );

    const saveDraft = useCallback(async (duplicateAction = null) => {
        setIsSaving(true);
        try {
            const cleanDraft = sanitizeCvDraft(draft);
            
            // If it's a new draft, use createCVDraft directly to handle deduplication
            if (!draftId) {
                const response = await createCVDraft(cleanDraft, duplicateAction);
                if (response?.duplicate_detected) {
                    setDuplicatePrompt({
                        show: true,
                        existingDrafts: response.existing_drafts || [],
                        existingCv: response.existing_cv || (response.existing_drafts?.[0] ? {
                            id: response.existing_drafts[0]._id,
                            candidate_name: response.existing_drafts[0].candidate_name,
                            title: response.existing_drafts[0].title,
                            status: response.existing_drafts[0].status,
                            updated_at: response.existing_drafts[0].updated_at,
                            last_generated_at: response.existing_drafts[0].last_generated_at,
                        } : null),
                        newCv: response.new_cv || {
                            title: cleanDraft?.candidate?.title,
                            candidate_name: cleanDraft?.candidate?.full_name,
                        },
                        candidateName: response.candidate_name || cleanDraft?.candidate?.full_name,
                        payload: cleanDraft
                    });
                    setIsSaving(false);
                    return null; // Stop saving flow to show modal
                }
                
                const savedId = response?._id;
                if (savedId) {
                    setDraftId(savedId);
                    setContentRevision(response?.content_revision || 1);
                    if (response?.draft) {
                        latestSavedDraftRef.current = response.draft;
                        setDraft(normalizeCvDraft(response.draft));
                    }
                    setStatus(response?.status || "draft");
                    setCompletionScore(response?.completionScore || 0);
                    toast.success("Brouillon créé");
                    return savedId;
                }
                return null;
            } else {
                // Regular update
                const saved = await persistDraft({
                    draftId,
                    draft: cleanDraft,
                    expectedRevision: contentRevision,
                });
                if (saved?.draft) {
                    latestSavedDraftRef.current = saved.draft;
                    setDraft(normalizeCvDraft(saved.draft));
                }
                setContentRevision(saved?.contentRevision || contentRevision);
                setStatus(saved?.status || "draft");
                setCompletionScore(saved?.completionScore || 0);
                toast.success("Brouillon mis à jour");
                return saved?.draftId || draftId;
            }
        } catch (error) {
            console.error("Error saving draft", error);
            if (error?.response?.status === 409) {
                toast.error("Conflit de modification : une version plus récente du CV existe sur le serveur. Veuillez recharger la page.");
            } else if (error?.response?.status === 401) {
                saveFormBackup(`cv_builder_draft_${draftId || "current"}`, draft);
                toast.error("Session expirée. Vos modifications ont été conservées pour cet onglet uniquement.");
            } else {
                toast.error("Erreur lors de la sauvegarde");
            }
            return null;
        } finally {
            setIsSaving(false);
        }
    }, [draft, draftId, contentRevision]);

    const executeJobPollingAndCompletion = useCallback(async (jobId, startedAt) => {
        let rawResult;
        let consecutivePollErrors = 0;

        try {
            while (true) {
                await new Promise((resolve) => setTimeout(resolve, CV_PARSE_POLLING_INTERVAL_MS));
                let statusResponse = null;
                try {
                    statusResponse = await getParseJobStatus(jobId);
                    consecutivePollErrors = 0;
                } catch (pollErr) {
                    consecutivePollErrors += 1;
                    const errStatus = pollErr?.response?.status;
                    const isFatal = errStatus === 404 || errStatus === 403;
                    if (isFatal || consecutivePollErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
                        try {
                            localStorage.removeItem(jobStorageKey);
                        } catch {
                            /* ignore storage error */
                        }
                        const errPayload = {
                            success: false,
                            error_code: isFatal ? (errStatus === 403 ? "job_forbidden" : "job_not_found") : "JOB_STATUS_UNAVAILABLE",
                            error_message: isFatal
                                ? "Le job d'analyse n'est plus accessible ou a expiré."
                                : "Impossible de contacter le serveur pour suivre l'analyse du CV.",
                        };
                        const mockError = new Error(errPayload.error_message);
                        mockError.response = { data: errPayload };
                        throw mockError;
                    }
                    await new Promise((resolve) => setTimeout(resolve, Math.min(consecutivePollErrors * 800, 3000)));
                    continue;
                }

                if (statusResponse?.progress) {
                    applyImportProgress(statusResponse.progress);
                }

                if (statusResponse?.status === "completed") {
                    rawResult = statusResponse.result;
                    try {
                        localStorage.removeItem(jobStorageKey);
                    } catch {
                        /* ignore storage error */
                    }
                    break;
                } else if (statusResponse?.status === "failed") {
                    try {
                        localStorage.removeItem(jobStorageKey);
                    } catch {
                        /* ignore storage error */
                    }
                    if (statusResponse?.progress) {
                        applyImportProgress(statusResponse.progress);
                    }
                    const errorPayload = statusResponse.error || {
                        success: false,
                        error_code: "job_failed",
                        error_message: "L'analyse a échoué lors de l'exécution en arrière-plan."
                    };
                    const mockError = new Error(errorPayload.error_message || "Job failed");
                    mockError.response = { data: errorPayload };
                    throw mockError;
                }
            }

            const result = normalizeImportResponse(rawResult);
            setLastImportResult(result);
            setCacheMetadata(rawResult?.cache || null);

            if (!result.success || !result.draft) {
                const failedResult = {
                    ...result,
                    success: false,
                    draft: null,
                    error_code: result.error_code || "cv_parse_failed",
                    error_message: result.error_message || "L'analyse du CV a échoué. Veuillez réessayer.",
                };
                setLastImportResult(failedResult);
                setParseScore(failedResult.confidence_score || 0);
                setImportWarnings(failedResult.warnings || []);
                setManualReviewRequired(failedResult.manual_review_required === true);
                setImportError(failedResult);
                setImportMode("upload");

                toast.error(failedResult.error_message, { duration: 5000 });
                if (failedResult.warnings.length > 0) {
                    logCvImportTelemetry("warning", {
                        duration_ms: Date.now() - startedAt,
                        warning_count: failedResult.warnings.length,
                        quality_status: failedResult.quality_status,
                        manual_review_required: failedResult.manual_review_required,
                    }, "warn");
                }
                logCvImportTelemetry("error", {
                    duration_ms: Date.now() - startedAt,
                    error_code: failedResult.error_code,
                    quality_status: failedResult.quality_status,
                }, "warn");
                return null;
            }

            setDraft((currentDraft) => mergeImportedDraftPreservingUserEdits(currentDraft, result.draft));
            if (result.draft_id) {
                setDraftId(result.draft_id);
                setContentRevision(Number(result.content_revision || rawResult?.content_revision) || 1);
                setStatus("draft");
            }
            setParseScore(result.extraction_fidelity || result.confidence_score || 0);
            setVerificationProgress(result.verification_progress || computeVerificationProgress(result.anomalies || []));
            setImportWarnings(result.warnings || []);
            setManualReviewRequired(result.manual_review_required === true);
            setValidationStatus(result.validation_status || "READY");
            setReviewConfirmed(false);
            setImportError(null);
            setAnomalies(result.anomalies || []);
            setScoringDimensions(result.scoring_dimensions || null);
            setImportMode("scoring");
            logCvImportTelemetry("success", {
                duration_ms: Date.now() - startedAt,
                quality_status: result.quality_status,
                warning_count: result.warnings.length,
                manual_review_required: result.manual_review_required,
                confidence_score: result.confidence_score,
            });
            if (result.warnings.length > 0) {
                logCvImportTelemetry("warning", {
                    duration_ms: Date.now() - startedAt,
                    warning_count: result.warnings.length,
                    quality_status: result.quality_status,
                    manual_review_required: result.manual_review_required,
                }, "warn");
            }
            return result;
        } catch (error) {
            try {
                localStorage.removeItem(jobStorageKey);
            } catch {
                /* ignore storage error */
            }
            const normalizedError = normalizeImportError(error);
            setParseScore(normalizedError.confidence_score || 0);
            setImportWarnings(normalizedError.warnings || []);
            setManualReviewRequired(normalizedError.manual_review_required === true);
            setImportError(normalizedError);
            setLastImportResult(normalizedError);
            setImportMode("upload");
            toast.error(normalizedError.error_message, { duration: 5000 });
            logAppError("cv_builder_import", error, {
                error_code: normalizedError.error_code,
                quality_status: normalizedError.quality_status,
                warning_count: normalizedError.warnings.length,
            });
            if (normalizedError.warnings.length > 0) {
                logCvImportTelemetry("warning", {
                    duration_ms: Date.now() - startedAt,
                    warning_count: normalizedError.warnings.length,
                    quality_status: normalizedError.quality_status,
                    manual_review_required: normalizedError.manual_review_required,
                }, "warn");
            }
            logCvImportTelemetry("error", {
                duration_ms: Date.now() - startedAt,
                error_code: normalizedError.error_code,
                quality_status: normalizedError.quality_status,
            }, "error");
            return null;
        } finally {
            setIsUploading(false);
        }
    }, [setDraft, applyImportProgress, jobStorageKey]);

    const parseUploadedCv = useCallback(async (file) => {
        const startedAt = Date.now();
        logCvImportTelemetry("start", {
            file_size_bytes: file?.size || 0,
            file_type: getFileExtension(file?.name || ""),
        });

        setIsUploading(true);
        setImportError(null);
        setImportWarnings([]);
        setLastImportResult(null);
        setImportProgress(null);
        setImportMode("uploading");
        try {
            const jobResponse = await createParseJob(file, forceRefresh);
            const jobId = jobResponse.job_id;
            if (jobResponse?.progress) {
                applyImportProgress(jobResponse.progress);
            }
            try {
                localStorage.setItem(
                    jobStorageKey,
                    JSON.stringify({ jobId, startedAt, filename: file?.name || "CV" })
                );
            } catch {
                /* ignore storage error */
            }

            return await executeJobPollingAndCompletion(jobId, startedAt);
        } catch (error) {
            try {
                localStorage.removeItem(jobStorageKey);
            } catch {
                /* ignore storage error */
            }
            const normalizedError = normalizeImportError(error);
            setParseScore(normalizedError.confidence_score || 0);
            setImportWarnings(normalizedError.warnings || []);
            setManualReviewRequired(normalizedError.manual_review_required === true);
            setImportError(normalizedError);
            setLastImportResult(normalizedError);
            setImportMode("upload");
            toast.error(normalizedError.error_message, { duration: 5000 });
            setIsUploading(false);
            return null;
        }
    }, [forceRefresh, executeJobPollingAndCompletion, applyImportProgress, jobStorageKey]);

    // Resilient page reload resumption (Phase 7)
    useEffect(() => {
        let isCancelled = false;
        const resumeActiveJob = async () => {
            try {
                const raw = localStorage.getItem(jobStorageKey);
                if (!raw) return;
                const saved = JSON.parse(raw);
                if (!saved?.jobId) {
                    localStorage.removeItem(jobStorageKey);
                    return;
                }
                if (Date.now() - (saved.startedAt || 0) > 20 * 60 * 1000) {
                    localStorage.removeItem(jobStorageKey);
                    return;
                }
                const statusResponse = await getParseJobStatus(saved.jobId);
                if (isCancelled) return;
                if (statusResponse?.status === "queued" || statusResponse?.status === "running") {
                    setIsUploading(true);
                    setImportMode("uploading");
                    if (statusResponse.progress) {
                        applyImportProgress(statusResponse.progress);
                    }
                    executeJobPollingAndCompletion(saved.jobId, saved.startedAt || Date.now());
                } else {
                    localStorage.removeItem(jobStorageKey);
                }
            } catch {
                try {
                    localStorage.removeItem(jobStorageKey);
                } catch {
                    /* ignore storage error */
                }
            }
        };
        if (resumeImport) resumeActiveJob();
        return () => {
            isCancelled = true;
        };
    }, [executeJobPollingAndCompletion, applyImportProgress, resumeImport, jobStorageKey]);

    const resetImportState = useCallback(() => {
        try {
            localStorage.removeItem(jobStorageKey);
        } catch {
            /* ignore storage error */
        }
        setImportMode("none");
        setIsUploading(false);
        setImportProgress(null);
        setImportError(null);
        setParseScore(0);
        setVerificationProgress({ total: 0, treated: 0, remaining: 0, percentage: 100, is_complete: true });
        setImportWarnings([]);
        setManualReviewRequired(false);
        setValidationStatus("READY");
        setLastImportResult(null);
        setReviewConfirmed(false);
        setUserValidated(false);
        setCacheMetadata(null);
    }, [jobStorageKey]);

    const generateDraft = useCallback(async (format = "pdf") => {
        let currentDraftId = draftId;
        if (!currentDraftId) {
            currentDraftId = await saveDraft();
            if (!currentDraftId) return null;
        } else {
            const savedDraftId = await saveDraft();
            if (!savedDraftId) {
                toast.error("Le CV n’a pas pu être enregistré. L’export a été annulé pour éviter de télécharger une ancienne version.");
                return null;
            }
            currentDraftId = savedDraftId;
        }

        try {
            const normalizedFormat = format === "docx" ? "docx" : "pdf";
            const blob = normalizedFormat === "docx"
                ? await requestGenerateDocx(currentDraftId)
                : await requestGeneratePdf(currentDraftId);
            const fileName = normalizedFormat === "docx" ? buildDocxFileName(draft) : buildPdfFileName(draft);
            setStatus("generated");
            toast.success("CV généré avec succès");
            return {
                blob,
                fileName,
                draftId: currentDraftId,
            };
        } catch (error) {
            const exportError = normalizeApiError(
                error,
                `Erreur lors de la génération du ${format === "docx" ? "document Word" : "PDF"}`,
            );
            logAppError("cv_builder.export", error, {
                format,
                draft_id: currentDraftId,
                backend_detail: exportError.backendMessage || null,
            });
            toast.error(exportError.message);
            return null;
        }
    }, [draft, draftId, saveDraft]);

    const validateDraft = useCallback(async () => {
        const currentDraftId = await saveDraft();
        if (!currentDraftId) return false;
        try {
            await requestUserValidation(currentDraftId);
            setUserValidated(true);
            setReviewConfirmed(true);
            return true;
        } catch (error) {
            console.error("Error validating CV for export", error);
            toast.error("La validation du CV a échoué");
            return false;
        }
    }, [saveDraft]);

    const previewDraftPdf = useCallback(async () => {
        let currentDraftId = draftId;
        try {
            const savedId = await saveDraft();
            if (savedId) {
                currentDraftId = savedId;
            }
        } catch {
            // Proceed even if save failed
        }

        try {
            const draftToPreview = latestSavedDraftRef.current || draft;
            const cleanDraft = sanitizeCvDraft(draftToPreview);
            let blob;
            try {
                blob = await requestPreviewPdfPayload(cleanDraft);
            } catch (payloadError) {
                if (currentDraftId) {
                    blob = await requestPreviewPdf(currentDraftId);
                } else {
                    throw payloadError;
                }
            }
            const safeBlob = blob instanceof Blob ? blob : new Blob([blob], { type: "application/pdf" });
            const pages = Number(blob?.headers?.["x-total-pages"]);
            if (pages && Number.isFinite(pages)) {
                setPreviewPageCount(pages);
            }
            return window.URL.createObjectURL(safeBlob);
        } catch (error) {
            const previewError = normalizeApiError(error);
            logAppError("cv_builder.preview", error, {
                endpoint: currentDraftId ? `/api/recruitment/cv-builder/drafts/${currentDraftId}/preview` : "/api/recruitment/cv-builder/preview",
                backend_detail: previewError.backendMessage || null,
            });
            toast.error("Erreur lors de la génération de la preview PDF");
            throw error;
        }
    }, [draft, draftId, saveDraft]);

    const resetDraft = useCallback(() => {
        setDraft(createDefaultCvDraft());
        setDraftId(null);
        setContentRevision(null);
        setStatus("new");
        setCompletionScore(0);
        setIsUploading(false);
        setImportError(null);
        setParseScore(0);
        setVerificationProgress({ total: 0, treated: 0, remaining: 0, percentage: 100, is_complete: true });
        setImportWarnings([]);
        setManualReviewRequired(false);
        setValidationStatus("READY");
        setLastImportResult(null);
        setReviewConfirmed(false);
        setImportMode("none");
        setCacheMetadata(null);
        setAnomalies([]);
        setScoringDimensions(null);
        clearLocalDraft();
        toast.success("Nouveau CV initialisé");
    }, []);

    const resolveAnomaly = useCallback((anomalyId, actionType, payload = {}) => {
        const item = payload?.item || (payload?.category ? payload : null);
        const justification = payload?.justification || payload?.reason || null;

        let targetStatus = STATUS_CONFIRMED;
        if (actionType === "reopen") {
            targetStatus = STATUS_REOPENED;
        } else if (actionType === "mark_verified" || actionType === "confirm") {
            targetStatus = STATUS_CONFIRMED;
        } else if (actionType === "accept_suggestion" || actionType === "correct") {
            targetStatus = STATUS_CORRECTED;
        } else if (actionType === "reassign") {
            targetStatus = STATUS_REASSIGNED;
        } else if (actionType === "dismiss") {
            targetStatus = STATUS_DISMISSED;
        }

        setAnomalies((prev) => {
            const next = prev.map((a) => {
                if (a.id !== anomalyId) return a;
                const historyEntry = {
                    action: actionType,
                    status: targetStatus,
                    timestamp: new Date().toISOString(),
                    justification: justification || (actionType === "dismiss" ? "Alerte non pertinente" : null),
                    previous_status: a.status,
                };
                const history = Array.isArray(a.history) ? [...a.history, historyEntry] : [historyEntry];
                return {
                    ...a,
                    status: targetStatus,
                    history,
                    justification: justification || a.justification || null,
                };
            });

            const progress = computeVerificationProgress(next);
            setVerificationProgress(progress);

            const remainingActive = next.filter((anomaly) => isAnomalyActive(anomaly.status));
            const hasBlocking = remainingActive.some((anomaly) =>
                anomaly.severity === "critical" ||
                [
                    "missing_significant_content",
                    "unsupported_fact",
                    "structural_misassignment",
                ].includes(anomaly.category)
            );
            // P2: Frontend can only move status DOWN (more restrictive),
            // never UP without backend revalidation.
            // CORRECTION_REQUIRED -> REVIEW_RECOMMENDED -> READY
            // We allow optimistic downgrade for UI responsiveness,
            // but the backend gate on POST /generate remains authoritative.
            const nextValidationStatus = hasBlocking
                ? "CORRECTION_REQUIRED"
                : (remainingActive.length > 0 ? "REVIEW_RECOMMENDED" : "READY");
            setValidationStatus(nextValidationStatus);
            setManualReviewRequired(hasBlocking);

            setDraft((current) => {
                const nextDraft = JSON.parse(JSON.stringify(current));
                nextDraft.source_metadata = nextDraft.source_metadata || {};
                const priorAnalysis = nextDraft.source_metadata.analysis || {};
                nextDraft.source_metadata.analysis = {
                    ...priorAnalysis,
                    anomalies: next,
                    validation_status: nextValidationStatus,
                    manual_review_required: hasBlocking,
                    verification_progress: progress,
                    extraction_fidelity: parseScore,
                };
                return nextDraft;
            });

            setScoringDimensions((prevDims) => {
                const recomputed = recomputeScoringFromAnomalies(prevDims, next);
                if (recomputed) {
                    return recomputed.nextDimensions;
                }
                return prevDims;
            });

            return next;
        });

        if ((actionType === "accept_suggestion" || actionType === "correct") && item?.auto_fix_available === true) {
            if (item.category === "unmapped_content") {
                setDraft((curr) => {
                    const copy = JSON.parse(JSON.stringify(curr));
                    const experienceIndex = Number(item?.target_ref?.experience_index);
                    const missionIndex = Number(item?.target_ref?.mission_index);
                    const mission = copy.experiences?.[experienceIndex]?.missions?.[missionIndex];
                    const sourceText = String(item.text_raw || "").trim();
                    if (!mission || !sourceText) return curr;
                    mission.responsibilities = Array.isArray(mission.responsibilities) ? mission.responsibilities : [];
                    if (!mission.responsibilities.some((value) => String(value).trim().toLowerCase() === sourceText.toLowerCase())) {
                        mission.responsibilities.push(sourceText);
                    }
                    mission.responsibility_provenance = Array.isArray(mission.responsibility_provenance)
                        ? mission.responsibility_provenance
                        : [];
                    mission.responsibility_provenance.push({
                        text: sourceText,
                        source_refs: item.source_refs || [],
                        resolution: "added_from_quality_review",
                    });
                    copy.unmapped_content = (copy.unmapped_content || []).map((entry) => (
                        entry?.id === item.id
                            ? { ...entry, status: STATUS_CORRECTED, resolution: "added_to_mission" }
                            : entry
                    ));
                    copy.source_metadata = copy.source_metadata || {};
                    copy.source_metadata.field_provenance = copy.source_metadata.field_provenance || {};
                    copy.source_metadata.field_provenance[
                        `experiences[${experienceIndex}].missions[${missionIndex}].responsibilities`
                    ] = "USER_EDITED";
                    return copy;
                });
            } else if (item.category === "structural_misassignment") {
                setDraft((curr) => {
                    const copy = JSON.parse(JSON.stringify(curr));
                    (copy.experiences || []).forEach((exp) => {
                        (exp.missions || []).forEach((m) => {
                            if (Array.isArray(m.environment)) {
                                m.environment = m.environment.filter(
                                    (env) => String(env).trim().toLowerCase() !== String(item.current_value || item.text_raw).trim().toLowerCase()
                                );
                            }
                        });
                    });
                    copy.source_metadata = copy.source_metadata || {};
                    copy.source_metadata.field_provenance = copy.source_metadata.field_provenance || {};
                    if (item.field_path) {
                        copy.source_metadata.field_provenance[item.field_path] = "USER_EDITED";
                    }
                    return copy;
                });
            } else if (item.category === "unsupported_fact") {
                setDraft((curr) => {
                    const copy = JSON.parse(JSON.stringify(curr));
                    if (item.field_path && item.field_path.startsWith("skills")) {
                        (copy.skills?.categories || []).forEach((cat) => {
                            if (Array.isArray(cat.items)) {
                                cat.items = cat.items.filter(
                                    (it) => String(it).trim().toLowerCase() !== String(item.current_value).trim().toLowerCase()
                                );
                            }
                        });
                    }
                    copy.source_metadata = copy.source_metadata || {};
                    copy.source_metadata.field_provenance = copy.source_metadata.field_provenance || {};
                    if (item.field_path) {
                        copy.source_metadata.field_provenance[item.field_path] = "USER_EDITED";
                    }
                    return copy;
                });
            }
        }

        if (targetStatus === STATUS_CONFIRMED) {
            toast.success("Point confirmé par le recruteur");
        } else if (targetStatus === STATUS_DISMISSED) {
            toast.success("Alerte ignorée avec justification");
        } else if (targetStatus === STATUS_REOPENED) {
            toast.success("Alerte rouverte");
        } else if (targetStatus === STATUS_REASSIGNED) {
            toast.success("Information réaffectée");
        } else {
            toast.success("Correction enregistrée (USER_EDITED)");
        }
    }, [parseScore, setAnomalies, setDraft]);

    const candidateActions = useMemo(() => createCandidateActions(setDraft), [setDraft]);
    const skillsActions = useMemo(() => createSkillsActions(setDraft), [setDraft]);
    const experienceActions = useMemo(() => createExperienceActions(setDraft), [setDraft]);
    const profileActions = useMemo(() => createProfileActions(setDraft), [setDraft]);

    const contextValue = useMemo(() => ({
        draft,
        draftId,
        status,
        completionScore,
        isLoading,
        isSaving,
        importMode,
        isUploading,
        importProgress,
        setImportProgress,
        importError,
        importWarnings,
        anomalies,
        setAnomalies,
        scoringDimensions,
        resolveAnomaly,
        manualReviewRequired,
        validationStatus,
        lastImportResult,
        setImportMode,
        parseScore,
        extractionFidelity: parseScore,
        verificationProgress,
        parseWarnings: importWarnings,
        mandatoryReview: manualReviewRequired,
        userValidated,
        validateDraft,
        reviewConfirmed,
        setReviewConfirmed,
        loadDraft,
        saveDraft,
        generateDraft,
        previewDraftPdf,
        previewPageCount,
        loadLocalDraft,
        parseUploadedCv,
        resetImportState,
        forceRefresh,
        setForceRefresh,
        cacheMetadata,
        setCacheMetadata,
        duplicatePrompt,
        setDuplicatePrompt,
        ...candidateActions,
        ...skillsActions,
        ...experienceActions,
        ...profileActions,
        resetDraft, // Re-spread to ensure the full store reset wins over the partial one in actions
    }), [
        draft, draftId, status, completionScore, isLoading, isSaving, 
        importMode, isUploading, importProgress, importError, importWarnings, anomalies, scoringDimensions, resolveAnomaly, manualReviewRequired, validationStatus, lastImportResult, parseScore, verificationProgress, reviewConfirmed, userValidated, validateDraft,
        loadDraft, saveDraft, generateDraft, previewDraftPdf, previewPageCount, loadLocalDraft,
        parseUploadedCv, resetImportState, resetDraft,
        candidateActions, skillsActions, experienceActions, profileActions,
        cacheMetadata, duplicatePrompt, forceRefresh
    ]);

    return (
        <CvBuilderContext.Provider value={contextValue}>
            {children}
        </CvBuilderContext.Provider>
    );
};

export const useCvBuilder = () => {
    const context = useContext(CvBuilderContext);
    if (!context) {
        throw new Error("useCvBuilder must be used within a CvBuilderProvider");
    }
    return context;
};
